"use client";

import { Button } from "@/components/ui/button";
import { cn } from "@/utils/utils";
import { MailCheck } from "lucide-react";
import { useCountdown } from "@/hooks/use-countdown";

const RESEND_EMAIL_COUNTDOWN_SECONDS = 30;

interface ResendEmailLayoutProps {
  title: string;
  subtitle: React.ReactNode;
  onResendEmail: () => Promise<void>;
  withResendButton?: boolean;
}

export const ResendEmailLayout = ({
  title,
  subtitle,
  onResendEmail,
  withResendButton = true,
}: ResendEmailLayoutProps) => {
  const { countdown, startCountdown, isCountdownActive } = useCountdown({
    initialSeconds: RESEND_EMAIL_COUNTDOWN_SECONDS,
  });

  const handleResendEmail = async () => {
    try {
      await onResendEmail();
      startCountdown(RESEND_EMAIL_COUNTDOWN_SECONDS);
    } catch (error) {
      console.error("Error resending email:", error);
    }
  };

  return (
    <div className="flex w-full flex-col gap-8">
      <div className="flex flex-col gap-8">
        <div
          className={cn(
            "bg-button-outline relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-input shadow shadow-gray-100",
            "before:absolute before:h-12 before:w-12 before:rounded-full before:border before:opacity-60",
            "[&>svg>path:last-of-type]:stroke-green-500",
          )}
        >
          <MailCheck className="h-5 w-5 text-foreground" />
        </div>
        <div className="flex flex-col gap-3">
          <h1 className="text-2xl font-medium">{title}</h1>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      {withResendButton && (
        <div className="flex h-7 items-center gap-3">
          <span className="text-sm text-muted-foreground">
            Didn&apos;t get it?{" "}
          </span>
          <Button
            variant="outline"
            onClick={handleResendEmail}
            disabled={isCountdownActive}
            className="h-7 rounded-md px-2.5 text-[13px]"
          >
            {isCountdownActive
              ? `Resend email (${countdown}s)`
              : "Resend email"}
          </Button>
        </div>
      )}
    </div>
  );
};
